/*
==========================================
STAMP PATCH  v3.0.1
文件: assets/stamp-patch.js

v3.0.0 新增：Canvas 文字渲染覆盖层
  将所有使用 HTML/CSS DOM 渲染文字的布局
  （image-left / image-top / image-center / text-only / photo-upload）
  统一接入 StampCanvasTextRenderer，使用和产品9（address-label）
  完全一致的 Canvas 2D 字体渲染方式。

  核心效果：
  ✓ 花体连笔字（Great Vibes / Sacramento / Dancing Script 等）
    不再因 CSS letter-spacing 断开连笔
  ✓ 所有产品字体渲染行为统一

  实现方案：
  - 在每个 .prod-stamp-canvas 内叠加一层透明 <canvas>（overlay）
  - overlay 用 StampCanvasTextRenderer.drawLines() 渲染文字
  - 原 HTML span 文字隐藏（visibility:hidden），保留 DOM 供
    _fitImageLeftText / 文字拖拽 / 表单提交等逻辑继续使用
  - 每次 DOM 文字/字体/尺寸/颜色变化时自动重绘 overlay

  布局对照：
  image-left   → 读 .prod-text-container 位置，align='left'
  image-top    → 读 .prod-text-container 位置，align='center'
  image-center → 读 .prod-text-container 位置，align='center'
  text-only    → 读 .prod-text-container 位置，align='center'
  photo-upload → 有 .prod-text-container 时渲染；有 curved-text 时跳过

  不接管的布局：
  curved-text   → SVG 弧形文字，Core.generateCurvedText 处理，保持不变
  address-label → StampAddressComposer 全权管理，保持不变
  body-template → StampBodyComposer 全权管理，保持不变

v2.0.0 保留（内容不变）：
  - 顶部预览框 pinch zoom 支持
  - 照片同步修复
  - 长按3秒下载弹窗
==========================================
*/
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────
     工具
  ───────────────────────────────────────────────────── */
  function _getConfig() {
    try {
      var el = document.getElementById('prodStampConfig');
      return JSON.parse((el && el.textContent) || '{}');
    } catch (e) { return {}; }
  }

  function waitReady(cb) {
    var check = function () {
      if (window.StampCore && typeof window.StampCore.updatePhotoDisplay === 'function') {
        cb(window.StampCore);
      } else {
        setTimeout(check, 80);
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', check);
    } else {
      check();
    }
  }

  /* ═══════════════════════════════════════════════════════
     ★ v3.0.0  CANVAS 文字渲染覆盖层
     所有使用 HTML/CSS DOM 文字的布局统一接入
     StampCanvasTextRenderer
  ═══════════════════════════════════════════════════════ */

  /* 需要接管文字渲染的布局 */
  var TEXT_OVERLAY_LAYOUTS = ['image-left', 'image-top', 'image-center', 'text-only', 'photo-upload'];

  /* 每个 stampCanvas 对应的 overlay canvas 注册表 */
  var _overlayMap = {};   // canvasId → overlayCanvas
  var _rafPending = {};   // canvasId → boolean（防止重复 rAF）

  /**
   * 从 .prod-text-line 的 dataset 读取该行样式
   * stamp-ui.js 的 updateTextFont/Size/Weight/LetterSpacing
   * 全部写入 data-font / data-fontsize / data-fontweight / data-letterSpacing
   */
  function _getLineStyle(tl) {
    var font   = tl.dataset.font          || 'Montserrat';
    var size   = parseFloat(tl.dataset.fontsize)   || 22;
    var weight = parseInt(tl.dataset.fontweight)   || 700;
    var ls     = parseFloat(tl.dataset.letterSpacing) || 0;
    return { font: font, size: size, weight: weight, ls: ls };
  }

  /** 读取当前用户输入文字（优先 prodTextLine{i}，回退到 span 内容） */
  function _getLineText(stampCanvas, lineIndex, config) {
    /* 先从 input 拿（最准确） */
    var inp = document.getElementById('prodTextLine' + lineIndex);
    if (inp) {
      return inp.value; /* 空字符串也返回，让 renderer 决定是否显示占位 */
    }
    /* 没有 input（只读镜像 canvas），从 span 拿，去掉 placeholder */
    var tl = stampCanvas.querySelector('[data-line-index="' + lineIndex + '"]');
    var sp = tl && tl.querySelector('span');
    if (!sp) return '';
    var text        = sp.textContent.trim();
    var placeholder = (config.text_lines && config.text_lines[lineIndex] && config.text_lines[lineIndex].placeholder) || '';
    return (text === placeholder) ? '' : text;
  }

  /**
   * 计算文字区域的 x / maxWidth / canvasHeight（Canvas 逻辑坐标）
   * 对于 image-left：文字区在插图右侧
   * 对于其他布局：文字区等于整个 canvas 区域
   */
  function _getTextArea(stampCanvas, layout) {
    var cW = stampCanvas.offsetWidth  || stampCanvas.getBoundingClientRect().width  || 300;
    var cH = stampCanvas.offsetHeight || stampCanvas.getBoundingClientRect().height || 300;

    if (layout === 'image-left') {
      var illus = stampCanvas.querySelector('.prod-illustration-left');
      var illusW = illus ? (illus.offsetWidth || illus.getBoundingClientRect().width || 0) : 0;
      /* gap 与 stamp-ui.js _fitImageLeftText 保持一致：mobile 8px，desktop 用 CSS gap */
      var isMobile = window.innerWidth <= 768;
      var gap = isMobile ? 8 : 12;
      var padding = 20; /* 左右各 10px */
      var x       = illusW + gap + padding / 2;
      var maxW    = Math.max(40, cW - x - padding / 2);
      return { x: x, maxWidth: maxW, canvasHeight: cH };
    }

    /* image-top / image-center / text-only / photo-upload */
    var pad = Math.round(cW * 0.04);
    return { x: pad, maxWidth: Math.max(40, cW - pad * 2), canvasHeight: cH };
  }

  /**
   * 计算 overlay canvas 的 offsetY（文字整体拖拽偏移）
   * image-left 读整体 text-container 的 top/left
   */
  function _getTextOffset(stampCanvas, layout) {
    if (layout === 'image-left') {
      var tc = stampCanvas.querySelector('.prod-text-container');
      if (tc) {
        return {
          x: parseFloat(tc.style.left) || 0,
          y: parseFloat(tc.style.top)  || 0
        };
      }
    }
    return { x: 0, y: 0 };
  }

  /**
   * 根据布局决定对齐方式
   * image-left → 左对齐
   * 其他 → 居中
   */
  function _getAlign(layout) {
    return layout === 'image-left' ? 'left' : 'center';
  }

  /**
   * 判断是否是产品8样式的 address stamp（Name + 多行地址）
   * 用于决定 groupGap（第0行与其他行间隙）
   */
  function _isAddressStyle(config) {
    if (!config || !config.text_lines || config.text_lines.length < 2) return false;
    var l1label = (config.text_lines[1] && config.text_lines[1].label || '').toLowerCase();
    return /address|street|city|state|zip/.test(l1label);
  }

  /**
   * 核心重绘：把当前所有行的文字用 Canvas 渲染到 overlay
   */
  function _redraw(stampCanvas, overlayCanvas, config) {
    var layout = config.layout;
    var area   = _getTextArea(stampCanvas, layout);
    var offset = _getTextOffset(stampCanvas, layout);
    var align  = _getAlign(layout);

    /* 同步 overlay 尺寸到物理像素（支持 Retina） */
    var dpr = window.devicePixelRatio || 1;
    var cssW = stampCanvas.offsetWidth  || 300;
    var cssH = stampCanvas.offsetHeight || 300;

    if (overlayCanvas.width  !== Math.round(cssW * dpr) ||
        overlayCanvas.height !== Math.round(cssH * dpr)) {
      overlayCanvas.width  = Math.round(cssW * dpr);
      overlayCanvas.height = Math.round(cssH * dpr);
      overlayCanvas.style.width  = cssW + 'px';
      overlayCanvas.style.height = cssH + 'px';
    }

    var ctx = overlayCanvas.getContext('2d');
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    ctx.scale(dpr, dpr);

    /* 读取当前墨水颜色 */
    var inkColor = '#1a1a1a';
    var inkBtn = document.querySelector('.prod-ink-btn.active');
    if (inkBtn && window.StampCore && window.StampCore.INK_COLOR_MAP) {
      inkColor = window.StampCore.INK_COLOR_MAP[inkBtn.dataset.ink] || '#1a1a1a';
    }

    /* 收集所有行 */
    var textLineEls = stampCanvas.querySelectorAll('.prod-text-line');
    var lines = [];
    textLineEls.forEach(function (tl) {
      var i = parseInt(tl.dataset.lineIndex || tl.getAttribute('data-line-index') || '0');
      /* 跳过弧形文字行（curved-text 由 Core 处理） */
      if (tl.classList.contains('curved-top-curve') || tl.classList.contains('curved-bottom-curve')) return;
      var style = _getLineStyle(tl);
      var text  = _getLineText(stampCanvas, i, config);
      var lhFactor = (i === 0 && _isAddressStyle(config)) ? 1.15 : 1.2;
      lines.push({
        _index:          i,
        text:            text,
        font:            style.font,
        size:            style.size,
        weight:          style.weight,
        letterSpacing:   style.ls,
        color:           inkColor,
        lineHeightFactor: lhFactor
      });
    });

    /* 按行号升序排列（以防 DOM 顺序与 data-line-index 不一致） */
    lines.sort(function (a, b) { return a._index - b._index; });

    /* 构建 placeholder 文字 */
    var phLine    = config.text_lines && config.text_lines[0];
    var phText    = phLine && phLine.placeholder ? phLine.placeholder : '';
    var phFont    = lines[0] ? lines[0].font : 'Montserrat';
    var phSize    = lines[0] ? lines[0].size * 0.82 : 18;

    /* 调用共享渲染器 */
    window.StampCanvasTextRenderer.drawLines(ctx, {
      lines:            lines,
      x:                area.x + offset.x,
      maxWidth:         area.maxWidth,
      canvasHeight:     area.canvasHeight,
      align:            align,
      groupGap:         _isAddressStyle(config) ? 4 : 2,
      offsetY:          offset.y,
      placeholderText:  phText,
      placeholderFont:  phFont,
      placeholderSize:  phSize,
      placeholderColor: '#c8c0b8'
    });

    /* 重置 scale（防止下次累积） */
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  /** 节流重绘：用 rAF 合并同一帧内的多次触发 */
  function _scheduleRedraw(stampCanvas, overlayCanvas, config) {
    var id = stampCanvas.id || 'unknown';
    if (_rafPending[id]) return;
    _rafPending[id] = true;
    requestAnimationFrame(function () {
      _rafPending[id] = false;
      _redraw(stampCanvas, overlayCanvas, config);
    });
  }

  /**
   * 为单个 stampCanvas 安装 overlay canvas
   * 并通过 MutationObserver 监听 DOM 文字/样式变化自动重绘
   */
  function _installOverlay(stampCanvas, config) {
    if (!stampCanvas || stampCanvas.dataset.canvasOverlayInstalled) return;
    stampCanvas.dataset.canvasOverlayInstalled = '1';

    /* overlay 必须 position:relative 的父容器才能叠加 */
    var curPos = getComputedStyle(stampCanvas).position;
    if (curPos === 'static') stampCanvas.style.position = 'relative';

    /* 创建透明 overlay canvas */
    var ov = document.createElement('canvas');
    ov.setAttribute('data-text-overlay', '1');
    ov.style.cssText = [
      'position:absolute',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'pointer-events:none',  /* 不拦截任何点击/拖拽事件 */
      'z-index:10',           /* 浮在 illustration 和 span 之上 */
      'border-radius:inherit' /* 跟随印章形状圆角 */
    ].join(';');

    stampCanvas.appendChild(ov);
    _overlayMap[stampCanvas.id || stampCanvas.getAttribute('data-canvas-id') || Math.random()] = ov;

    /* 隐藏 DOM 原始 span 文字（保留 DOM 结构，只是视觉上透明）*/
    /* 立即执行一次，再用 rAF 补一次，防止字体加载导致重绘后重新显示 */
    _hideOriginalSpans(stampCanvas);
    requestAnimationFrame(function () { _hideOriginalSpans(stampCanvas); });
    setTimeout(function () { _hideOriginalSpans(stampCanvas); }, 300);
    setTimeout(function () { _hideOriginalSpans(stampCanvas); }, 800);

    /* 首次渲染 */
    _redraw(stampCanvas, ov, config);

    /* MutationObserver：监听 span 内容 & data-* 属性变化 */
    var mo = new MutationObserver(function () {
      _scheduleRedraw(stampCanvas, ov, config);
    });
    mo.observe(stampCanvas, {
      subtree:       true,
      characterData: true,
      childList:     true,
      attributes:    true,
      attributeFilter: ['data-font', 'data-fontsize', 'data-fontweight', 'data-letter-spacing', 'style']
    });

    /* 窗口 resize 时重绘（因为 image-left 文字区宽度依赖插图宽度） */
    window.addEventListener('resize', function () {
      _scheduleRedraw(stampCanvas, ov, config);
    });

    /* 墨水/字体按钮点击 → 重绘（MutationObserver 可能检测不到 class 变化） */
    document.addEventListener('click', function (e) {
      if (e.target.closest('.prod-ink-btn') || e.target.closest('.prod-font-btn')) {
        setTimeout(function () { _scheduleRedraw(stampCanvas, ov, config); }, 50);
      }
    });

    /* 文字输入框 input 事件 → 重绘（直接监听，比 MO 更快） */
    document.querySelectorAll('[id^="prodTextLine"]').forEach(function (inp) {
      inp.addEventListener('input', function () {
        _scheduleRedraw(stampCanvas, ov, config);
      });
    });
  }

  /**
   * 隐藏原始 HTML span 文字（视觉上透明，不影响布局/宽度测量）
   * _fitImageLeftText 仍然依赖 scrollWidth 测量字体宽度，
   * 所以只改 color/opacity 而不改 visibility/display
   */
  function _hideOriginalSpans(stampCanvas) {
    stampCanvas.querySelectorAll('.prod-text-line span').forEach(function (sp) {
      /* 跳过弧形文字 span（这些由 Core 管理） */
      if (sp.classList.contains('prod-curved-text')) return;
      sp.style.cssText += ';color:transparent!important;opacity:0!important;';
    });
  }

  /**
   * 入口：初始化所有需要 overlay 的 stamp canvas
   */
  function _initCanvasTextOverlay() {
    /* 前置检查：渲染器必须已加载 */
    if (typeof window.StampCanvasTextRenderer === 'undefined') {
      console.warn('[StampPatch v3] StampCanvasTextRenderer not loaded. Canvas text overlay skipped.');
      console.warn('[StampPatch v3] 请在 stamp-customizer.liquid 中确保 stamp-canvas-text-renderer.js 在 stamp-patch.js 之前加载。');
      return;
    }

    var config = _getConfig();

    /* 只对需要接管的布局生效 */
    if (TEXT_OVERLAY_LAYOUTS.indexOf(config.layout) === -1) return;

    /* 获取所有 stamp canvas（包括镜像预览） */
    var canvases = [];
    ['prodStampCanvas', 'prodMobileCanvasBottom', 'prodMobileCanvasTop'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) canvases.push(el);
    });

    canvases.forEach(function (c) {
      _installOverlay(c, config);
    });

    /* 监听动态插入的 canvas（如手机端 MobileCanvasTop 延迟渲染） */
    var mo = new MutationObserver(function () {
      ['prodMobileCanvasTop'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el && !el.dataset.canvasOverlayInstalled) {
          _installOverlay(el, config);
        }
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    /* 延迟重绘兜底（等字体 Google Fonts 加载完成） */
    [500, 1200, 2500].forEach(function (ms) {
      setTimeout(function () {
        canvases.forEach(function (c) {
          var ov = c.querySelector('canvas[data-text-overlay]');
          if (ov) _redraw(c, ov, config);
        });
      }, ms);
    });

    /* image-left：_fitImageLeftText 完成后追加一次重绘
       因为 _fitImageLeftText 会改变 span 字号（数据源），
       MO 会捕获但为了确保时序正确，额外延迟 16ms 重绘 */
    if (config.layout === 'image-left') {
      var origFit = window._fitImageLeftTextDone;
      /* Monkey-patch：stamp-ui.js 内 _fitImageLeftText 调用了 _updateDomDebugPanel，
         借用此时机通过轮询来判断 fit 是否完成（无侵入方案） */
      setTimeout(function () {
        canvases.forEach(function (c) {
          var ov = c.querySelector('canvas[data-text-overlay]');
          if (ov) _redraw(c, ov, config);
        });
      }, 550);
      setTimeout(function () {
        canvases.forEach(function (c) {
          var ov = c.querySelector('canvas[data-text-overlay]');
          if (ov) _redraw(c, ov, config);
        });
      }, 1300);
    }
  }

  /* ═══════════════════════════════════════════════════════
     v2.0.0 保留：照片同步、长按下载、顶部 pinch zoom
  ═══════════════════════════════════════════════════════ */

  function _syncTopCanvas(processedImage) {
    var top = document.getElementById('prodMobileCanvasTop');
    if (!top) return;
    var layout = top.dataset.layout || '';
    if (layout === 'curved-text') {
      var cp = top.querySelector('.prod-illustration-center.prod-photo-preview');
      if (cp) cp.innerHTML = '<img src="' + processedImage + '" alt="Your photo" style="width:100%;height:100%;object-fit:cover;">';
    } else if (layout === 'photo-upload') {
      var pa = top.querySelector('.prod-photo-preview-area');
      if (pa) pa.innerHTML = '<img src="' + processedImage + '" alt="Your photo" style="width:100%;height:100%;object-fit:contain;border-radius:inherit;">';
    } else if (layout === 'image-left') {
      // ★ Fix: 同步 image-left 布局的左侧插图区
      var il = top.querySelector('.prod-illustration-left');
      if (il) {
        var img = il.querySelector('img.prod-illustration-img');
        if (!img) {
          img = document.createElement('img');
          img.className = 'prod-illustration-img';
          img.alt = 'Your photo';
          img.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;pointer-events:none;';
          il.appendChild(img);
        }
        img.src = processedImage;
      }
    } else if (layout === 'image-top') {
      // ★ Fix: 同步 image-top 布局的顶部插图区
      var it = top.querySelector('.prod-illustration-top');
      if (it) {
        var imgT = it.querySelector('img.prod-illustration-img');
        if (!imgT) {
          imgT = document.createElement('img');
          imgT.className = 'prod-illustration-img';
          imgT.alt = 'Your photo';
          imgT.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;pointer-events:none;';
          it.appendChild(imgT);
        }
        imgT.src = processedImage;
      }
    } else if (layout === 'image-center') {
      // ★ Fix: 同步 image-center 布局的居中插图区
      var ic = top.querySelector('.prod-illustration-center');
      if (ic) {
        var imgC = ic.querySelector('img.prod-illustration-img');
        if (!imgC) {
          imgC = document.createElement('img');
          imgC.className = 'prod-illustration-img';
          imgC.alt = 'Your photo';
          imgC.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;pointer-events:none;';
          ic.appendChild(imgC);
        }
        imgC.src = processedImage;
      }
    }
  }

  window._syncMobilePhotoMirror = function () {
    var main = document.getElementById('prodStampCanvas');
    var top  = document.getElementById('prodMobileCanvasTop');
    if (!main || !top) return;
    var img = main.querySelector('.prod-photo-preview-area img') ||
              main.querySelector('.prod-illustration-center.prod-photo-preview img') ||
              main.querySelector('.prod-illustration-left img.prod-illustration-img') ||
              main.querySelector('.prod-illustration-top img.prod-illustration-img') ||
              main.querySelector('.prod-illustration-center img.prod-illustration-img');
    if (img && img.src) _syncTopCanvas(img.src);
  };

  function _patchUpdatePhotoDisplay(Core) {
    if (Core.updatePhotoDisplay._patchedV19) return;
    var original = Core.updatePhotoDisplay;
    Core.updatePhotoDisplay = function (processedImage) {
      original(processedImage);
      _syncTopCanvas(processedImage);
    };
    Core.updatePhotoDisplay._patchedV19 = true;
  }

  function _initPreviewSync(Core) {
    var config = _getConfig();
    if (config.layout === 'address-label' || config.layout === 'body-template') return;

    if (!Core.getAllCanvases._patchedV19) {
      var orig = Core.getAllCanvases.bind(Core);
      Core.getAllCanvases = function () {
        var list = orig();
        ['prodMobileCanvasTop'].forEach(function (id) {
          var el = document.getElementById(id);
          if (el && list.indexOf(el) === -1) list.push(el);
        });
        return list;
      };
      Core.getAllCanvases._patchedV19    = true;
      Core.getAllCanvases._patchedV18    = true;
      Core.getAllCanvases._topPatched    = true;
      Core.getAllCanvases._bottomPatched = true;
    }

    var top = document.getElementById('prodMobileCanvasTop');
    if (top) top.style.pointerEvents = 'none';

    requestAnimationFrame(function () {
      var activeInk = document.querySelector('.prod-ink-btn.active');
      if (activeInk && Core.INK_COLOR_MAP) {
        var inkName = activeInk.dataset.ink || '';
        var color   = Core.INK_COLOR_MAP[inkName] || '#000000';
        Core.getAllCanvases().forEach(function (c) {
          if (!c) return;
          c.querySelectorAll('.prod-text-line').forEach(function (l) { l.style.color = color; });
        });
        var cls = {
          'Black':'ink-black','Red':'ink-red','Blue':'ink-blue',
          'Green':'ink-green','Purple':'ink-purple','Brown':'ink-brown',
          'No ink-stamp only':'ink-black'
        }[inkName] || 'ink-black';
        document.querySelectorAll('.prod-illustration-img').forEach(function (img) {
          img.classList.remove('ink-black','ink-red','ink-blue','ink-green','ink-purple','ink-brown');
          img.classList.add(cls);
        });
      }

      var activeBorder = document.querySelector('.prod-border-btn.active');
      if (activeBorder) {
        var bs = activeBorder.dataset.border || 'none';
        var allCls = [
          'border-none','border-simple','border-double','border-dashed','border-dotted',
          'border-double-thin','border-floral','border-floral2','border-floral3',
          'border-thick','border-double-thick','border-dots-lg','border-dash-lg',
          'border-vine','border-corners'
        ];
        Core.getAllCanvases().forEach(function (c) {
          if (!c) return;
          c.classList.remove.apply(c.classList, allCls);
          if (bs && bs !== 'none') c.classList.add('border-' + bs);
        });
      }

      var cfg = _getConfig();
      if ((cfg.layout === 'curved-text' || cfg.layout === 'photo-upload') &&
          typeof Core.generateCurvedText === 'function') {
        ['prodMobileCanvasTop'].forEach(function (id) {
          var el = document.getElementById(id);
          // regenerateCurvedText 先删旧SVG再画，防止重影
          if (el) setTimeout(function () { Core.regenerateCurvedText(el); }, 300);
        });
      }
    });
  }

  /* ── 顶部预览 pinch zoom（v2.0.0） ── */
  function _initTopPreviewPinchZoom() {
    var config = _getConfig();
    if (config.layout === 'address-label' || config.layout === 'body-template' || config.layout === 'text-only') return;

    var topWrapper = document.querySelector('.prod-mobile-preview-canvas--top');
    if (!topWrapper || topWrapper.dataset.patchPinchAttached) return;
    topWrapper.dataset.patchPinchAttached = '1';

    var _pinchActive = false, _pinchStartDist = 0, _pinchStartPct = 75;

    function getTouchDist(t) {
      var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }
    function getCurrentPct() {
      var sl = document.getElementById('prodPhotoSizeSliderNew') || document.getElementById('prodPhotoSizeSlider');
      return sl ? parseInt(sl.value) : 75;
    }
    function applyPct(v) {
      var sl = document.getElementById('prodPhotoSizeSliderNew') || document.getElementById('prodPhotoSizeSlider');
      var origSl = document.getElementById('prodPhotoSizeSlider');
      if (!sl) return;
      v = Math.max(+sl.min || 20, Math.min(+sl.max || 150, v));
      sl.value = v;
      var valEl = document.getElementById('prodPhotoSizeValNew');
      if (valEl) valEl.textContent = v + '%';
      sl.style.setProperty('--fill', ((v - (+sl.min || 20)) / ((+sl.max || 150) - (+sl.min || 20)) * 100).toFixed(1) + '%');
      if (origSl && origSl !== sl) { origSl.value = v; origSl.dispatchEvent(new Event('input')); }
      else sl.dispatchEvent(new Event('input'));
    }

    topWrapper.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) { _pinchActive = true; _pinchStartDist = getTouchDist(e.touches); _pinchStartPct = getCurrentPct(); e.preventDefault(); }
    }, { passive: false });
    topWrapper.addEventListener('touchmove', function (e) {
      if (!_pinchActive || e.touches.length !== 2) return;
      e.preventDefault();
      applyPct(Math.round(_pinchStartPct * getTouchDist(e.touches) / _pinchStartDist));
    }, { passive: false });
    topWrapper.addEventListener('touchend',   function (e) { if (e.touches.length < 2) _pinchActive = false; });
    topWrapper.addEventListener('touchcancel', function () { _pinchActive = false; });
  }

  /* ── 长按3秒下载（v2.0.0） ── */
  function _initLongPressDownload(Core) {
    var HOLD_MS = 3000, PASSWORD = 'plantandpress';

    function openPasswordModal() {
      var overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:99999;display:flex;align-items:center;justify-content:center;';
      overlay.innerHTML =
        '<div style="background:#fff;border-radius:12px;padding:28px 24px;width:300px;box-shadow:0 8px 32px rgba(0,0,0,0.18);font-family:inherit;">' +
          '<div style="font-weight:700;font-size:16px;margin-bottom:6px;">Download Preview</div>' +
          '<div style="font-size:13px;color:#888;margin-bottom:16px;">Enter password to download</div>' +
          '<input id="_dlPwdInput" type="password" placeholder="Password" style="width:100%;box-sizing:border-box;padding:9px 12px;border:1.5px solid #d0c8c0;border-radius:8px;font-size:14px;outline:none;margin-bottom:10px;">' +
          '<div id="_dlPwdErr" style="color:#e53e3e;font-size:12px;min-height:18px;margin-bottom:8px;"></div>' +
          '<div style="display:flex;gap:10px;">' +
            '<button id="_dlCancelBtn" type="button" style="flex:1;padding:9px;border:1.5px solid #d0c8c0;border-radius:8px;background:#fff;font-size:13px;cursor:pointer;">Cancel</button>' +
            '<button id="_dlConfirmBtn" type="button" style="flex:1;padding:9px;border:none;border-radius:8px;background:#1a1a1a;color:#fff;font-size:13px;font-weight:600;cursor:pointer;">Download</button>' +
          '</div>' +
        '</div>';
      document.body.appendChild(overlay);

      var input   = overlay.querySelector('#_dlPwdInput');
      var errMsg  = overlay.querySelector('#_dlPwdErr');
      var confirm = overlay.querySelector('#_dlConfirmBtn');
      var cancel  = overlay.querySelector('#_dlCancelBtn');
      setTimeout(function () { input.focus(); }, 50);

      var tryDownload = function () {
        if (input.value !== PASSWORD) {
          errMsg.textContent = 'Incorrect password';
          input.style.borderColor = '#e53e3e';
          input.focus();
          return;
        }
        document.body.removeChild(overlay);
        var cfg = _getConfig(), p;
        if (cfg.layout === 'body-template' && window.StampBodyComposer &&
            typeof window.StampBodyComposer.getComposedDataUrl === 'function') {
          p = Promise.resolve(window.StampBodyComposer.getComposedDataUrl());
        } else if (cfg.layout === 'address-label' && window.StampAddressComposer &&
                   typeof window.StampAddressComposer.getDataUrl === 'function') {
          p = Promise.resolve(window.StampAddressComposer.getDataUrl());
        } else {
          // 用 StampUI._buildPreviewImage 合成（Canvas API，无 html2canvas 的 CSS 渲染问题）
          var tc = {};
          if (window.StampUI && typeof window.StampUI.handleFormSubmit === 'function') {
            // 收集文字行
            var tls = cfg.text_lines || [];
            tls.forEach(function (l, i) {
              var inp = document.getElementById('prodTextLine' + i);
              if (inp) tc[l.label] = inp.value.trim() || l.placeholder || '';
            });
          }
          console.log('[StampPatch] StampUI=', !!window.StampUI, 'buildPreviewImage=', !!(window.StampUI && window.StampUI.buildPreviewImage));
          p = (window.StampUI && typeof window.StampUI.buildPreviewImage === 'function')
            ? window.StampUI.buildPreviewImage(tc)
            : (Core && typeof Core.capturePreview === 'function')
              ? (console.log('[StampPatch] falling back to capturePreview'), Core.capturePreview())
              : Promise.resolve(null);
        }
        p.then(function (dataUrl) {
          console.log('[StampPatch] dataUrl result:', dataUrl ? ('length='+dataUrl.length) : 'NULL');
          if (!dataUrl) return;
          var a = document.createElement('a');
          a.href = dataUrl; a.download = 'stamp-preview.png'; a.style.display = 'none';
          document.body.appendChild(a); a.click();
          setTimeout(function () { a.remove(); }, 200);
        }).catch(function (e) { console.error('[StampPatch] Download failed:', e); });
      };

      confirm.addEventListener('click', tryDownload);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') tryDownload(); });
      cancel.addEventListener('click', function () { document.body.removeChild(overlay); });
      overlay.addEventListener('click', function (e) { if (e.target === overlay) document.body.removeChild(overlay); });
    }

    function attachLongPress(container) {
      if (!container || container.dataset.lpAttached) return;
      container.dataset.lpAttached = '1';
      container.style.cssText += ';-webkit-touch-callout:none;-webkit-user-select:none;user-select:none;';
      var _timer = null, _startX = 0, _startY = 0;
      function start(e) {
        if (e.touches && e.touches.length > 1) return;
        _startX = e.touches ? e.touches[0].clientX : e.clientX;
        _startY = e.touches ? e.touches[0].clientY : e.clientY;
        _timer  = setTimeout(openPasswordModal, HOLD_MS);
      }
      function cancel() { if (_timer) { clearTimeout(_timer); _timer = null; } }
      function onTouchMove(e) {
        if (!_timer) return;
        var t = e.touches[0];
        if (Math.abs(t.clientX - _startX) > 10 || Math.abs(t.clientY - _startY) > 10) cancel();
      }
      function blockCtx(e) { e.preventDefault(); }
      container.addEventListener('mousedown',   start);
      container.addEventListener('mouseup',     cancel);
      container.addEventListener('mouseleave',  cancel);
      container.addEventListener('contextmenu', blockCtx);
      container.addEventListener('touchstart', function (e) {
        if (e.touches.length > 1) { cancel(); return; }
        e.preventDefault(); start(e);
      }, { passive: false });
      container.addEventListener('touchmove',   onTouchMove, { passive: true });
      container.addEventListener('touchend',    cancel,      { passive: true });
      container.addEventListener('touchcancel', cancel,      { passive: true });
      container.querySelectorAll('img, canvas, svg').forEach(function (el) {
        el.addEventListener('contextmenu', blockCtx);
        el.setAttribute('draggable', 'false');
        if (el.tagName === 'CANVAS' && el.getAttribute('data-composer-canvas')) return;
        /* overlay canvas 也不需要拦截 */
        if (el.tagName === 'CANVAS' && el.getAttribute('data-text-overlay')) return;
        el.style.cssText += ';-webkit-touch-callout:none;-webkit-user-select:none;user-select:none;pointer-events:none;';
      });
    }

    function attachAll() {
      var top = document.querySelector('.prod-mobile-preview-canvas--top');
      if (top) attachLongPress(top);
      var bot = document.querySelector('.prod-mobile-preview-bottom .prod-mobile-preview-canvas');
      if (bot) attachLongPress(bot);
      document.querySelectorAll('.prod-preview-section .prod-stamp-canvas-wrapper').forEach(attachLongPress);
      var mainCanvas = document.getElementById('prodStampCanvas');
      if (mainCanvas) {
        var wrapper = mainCanvas.closest('.prod-preview-container') || mainCanvas.parentElement;
        if (wrapper) attachLongPress(wrapper);
      }
    }

    requestAnimationFrame(function () { setTimeout(attachAll, 500); });
    var observer = new MutationObserver(attachAll);
    var root = document.querySelector('.prod-form-section') ||
               document.querySelector('.prod-stamp-wrapper') ||
               document.body;
    observer.observe(root, { childList: true, subtree: true });
  }

  /* ════════════════════════════════════════════════════
     入口
  ════════════════════════════════════════════════════ */
  waitReady(function (Core) {
    _patchUpdatePhotoDisplay(Core);

    /* Canvas 文字 overlay 在所有 JS 加载完毕后初始化 */
    setTimeout(function () {
      _initCanvasTextOverlay();
      _initPreviewSync(Core);
      _initLongPressDownload(Core);
      _initTopPreviewPinchZoom();
    }, 600);

    /* MutationObserver 兜底：DOM 变化后重新尝试绑定 pinch zoom */
    var pinchObserver = new MutationObserver(function () {
      var topWrapper = document.querySelector('.prod-mobile-preview-canvas--top');
      if (topWrapper && !topWrapper.dataset.patchPinchAttached) {
        _initTopPreviewPinchZoom();
      }
    });
    pinchObserver.observe(document.body, { childList: true, subtree: true });
  });

})();